import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Badge,
  Tooltip,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useClientCountry } from '../../hooks/useClientCountry';
import { appointmentBookingPathForCountry } from '../../config/api';
import { CWY_COLORS } from '../../theme/colors';
import cwLogo from '../../assets/cw-red-logo.svg';
import cwFavicon from '../../assets/cw-favicon.svg';
import { CountrySelector } from './CountrySelector';
import { useAppTheme } from '../../contexts/ThemeContext';
import {
  formatMembershipTierLabel,
  useClientMembership,
} from '../../features/membership/useClientMembership';
import { MembershipAvatar } from '../../features/membership/MembershipAvatar';
import { MembershipTierBadge } from '../../features/membership/MembershipTierBadge';

/** Align with home `.page-container` and main content columns */
const LAYOUT_MAX_WIDTH = 1280;
const TOPBAR_HEIGHT = 52;
/** Below 1200px: larger touch-friendly icons; desktop: compact. */
const NAV_ICON_SX = {
  fontSize: { xs: 24, lg: 20 },
  width: { xs: 24, lg: 20 },
  height: { xs: 24, lg: 20 },
};

const NAV_ICON_SLOT_SX = {
  width: { xs: 24, lg: 20 },
  height: { xs: 24, lg: 20 },
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 0,
  '& .MuiSvgIcon-root': NAV_ICON_SX,
};

function navIcon(Icon: React.ElementType) {
  return (
    <Box sx={NAV_ICON_SLOT_SX}>
      <Icon sx={NAV_ICON_SX} />
    </Box>
  );
}

const TOPBAR_BADGE_SX = {
  '& .MuiBadge-badge': {
    bgcolor: '#cc1016',
    color: '#fff',
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1,
    minWidth: 18,
    height: 18,
    px: 0.5,
    borderRadius: '9px',
    top: 4,
    right: 2,
    transform: 'scale(1) translate(50%, -50%)',
    boxShadow: '0 0 0 2px var(--cwy-pageBg, #fff)',
  },
} as const;

function withNavBadge(icon: React.ReactNode, count: number) {
  if (count <= 0) return icon;
  return (
    <Badge
      badgeContent={count}
      max={99}
      overlap="rectangular"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={TOPBAR_BADGE_SX}
    >
      {icon}
    </Badge>
  );
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  match: (pathname: string) => boolean;
  bookingReturnPath?: string;
  badgeCount?: number;
}

export const AppTopbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { client, logout } = useAuth();
  const { threads } = useChat();
  const { unreadCount: notificationUnread } = useNotifications();
  const { country } = useClientCountry();
  const { isDark, setMode } = useAppTheme();
  const { membership, isLive } = useClientMembership({ syncFromServer: true });
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const membershipTierLabel = formatMembershipTierLabel(membership.tier);

  const appointmentsPath = appointmentBookingPathForCountry(country);

  const unreadMessages = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unread_count ?? 0), 0),
    [threads]
  );

  const navItems: NavItem[] = useMemo(
    () => [
      {
        label: 'Home',
        path: '/',
        icon: navIcon(HomeOutlinedIcon),
        match: (p) => p === '/',
      },
      {
        label: 'Store',
        path: '/store',
        icon: navIcon(ShoppingBagOutlinedIcon),
        match: (p) => p.startsWith('/store') && !p.startsWith('/store/canada-esim'),
      },
      {
        label: 'eSIM',
        path: '/store/canada-esim',
        icon: navIcon(SimCardOutlinedIcon),
        match: (p) => p.startsWith('/store/canada-esim'),
      },
      ...(country === 'CA'
        ? [
            {
              label: 'Vehicles',
              path: '/vehicles',
              icon: navIcon(DirectionsCarOutlinedIcon),
              match: (p: string) => p.startsWith('/vehicles'),
            },
          ]
        : []),
      {
        label: 'Appointments',
        path: appointmentsPath,
        icon: navIcon(EventAvailableOutlinedIcon),
        match: (p) => p.startsWith('/appointments'),
        bookingReturnPath: '/',
      },
      {
        label: 'Messaging',
        path: '/chat',
        icon: navIcon(ChatOutlinedIcon),
        match: (p) => p.startsWith('/chat') || p.startsWith('/community'),
        badgeCount: unreadMessages,
      },
      {
        label: 'Notifications',
        path: '/notifications',
        icon: navIcon(NotificationsNoneOutlinedIcon),
        match: (p) => p.startsWith('/notifications'),
        badgeCount: notificationUnread,
      },
    ],
    [appointmentsPath, country, unreadMessages, notificationUnread]
  );

  const handleLogout = async () => {
    setAccountAnchor(null);
    await logout();
    navigate('/login');
  };

  const handleNav = (item: NavItem) => {
    if (item.bookingReturnPath) {
      navigate(item.path, { state: { bookingReturnPath: item.bookingReturnPath } });
    } else {
      navigate(item.path);
    }
  };

  const navButtonSx = (active: boolean) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: 48, sm: 64 },
    maxWidth: { xs: 76, sm: 80 },
    height: TOPBAR_HEIGHT,
    px: 0.5,
    m: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: active ? CWY_COLORS.textPrimary : CWY_COLORS.textSecondary,
    borderBottom: active ? `2px solid ${CWY_COLORS.textPrimary}` : '2px solid transparent',
    borderRadius: 0,
    transition: 'color 0.15s ease',
    boxSizing: 'border-box',
    '&:hover': {
      color: CWY_COLORS.textPrimary,
    },
  });

  return (
    <>
    {/* ── Mobile left drawer ── */}
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sx={{
        display: { xs: 'block', sm: 'none' },
        '& .MuiDrawer-paper': {
          width: 260,
          bgcolor: isDark ? '#111' : '#fff',
          borderRight: `1px solid ${CWY_COLORS.border}`,
          pt: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {/* Drawer header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${CWY_COLORS.border}` }}>
        <Box component="img" src={cwLogo} alt="Canadawale Yar" sx={{ height: 28 }} />
        <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: CWY_COLORS.textSecondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Nav items */}
      <List disablePadding sx={{ py: 1 }}>
        {navItems.map((item) => {
          const active = item.match(location.pathname);
          const badgeCount = item.badgeCount ?? 0;
          let iconNode = item.icon;
          if (item.label === 'Notifications') {
            iconNode = active ? navIcon(NotificationsIcon) : navIcon(NotificationsNoneOutlinedIcon);
          }
          return (
            <ListItemButton
              key={item.label}
              selected={active}
              onClick={() => { setDrawerOpen(false); handleNav(item); }}
              sx={{
                px: 2.5,
                py: 1.25,
                borderRadius: '10px',
                mx: 1,
                mb: 0.25,
                color: active ? CWY_COLORS.textPrimary : CWY_COLORS.textSecondary,
                bgcolor: active ? (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)') : 'transparent',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                '&.Mui-selected': { bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                <Badge badgeContent={badgeCount} max={99} sx={TOPBAR_BADGE_SX}>
                  {iconNode}
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Country selector + theme in drawer */}
      <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CountrySelector variant="light" />
        <IconButton
          size="small"
          onClick={() => setMode(isDark ? 'light' : 'dark')}
          sx={{ color: CWY_COLORS.textPrimary, ml: 'auto' }}
          title={isDark ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
        >
          {isDark ? <Box component="span" sx={{ display: 'inline-block', transform: 'scaleX(-1)' }}>🌙</Box> : '☀️'}
        </IconButton>
      </Box>

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Account items in drawer */}
      <List disablePadding sx={{ py: 0.5 }}>
        <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/dashboard'); }} sx={{ px: 2.5, py: 1, mx: 1, borderRadius: '10px', color: CWY_COLORS.textSecondary }}>
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><PersonOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="My Account" primaryTypographyProps={{ fontSize: 14 }} />
        </ListItemButton>
        <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/membership'); }} sx={{ px: 2.5, py: 1, mx: 1, borderRadius: '10px', color: CWY_COLORS.textSecondary }}>
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><CardMembershipOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Membership" primaryTypographyProps={{ fontSize: 14 }} />
        </ListItemButton>
        <ListItemButton onClick={() => { setDrawerOpen(false); navigate('/profile/wallet'); }} sx={{ px: 2.5, py: 1, mx: 1, borderRadius: '10px', color: CWY_COLORS.textSecondary }}>
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><AccountBalanceWalletOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="My Crypto Wallet" primaryTypographyProps={{ fontSize: 14 }} />
        </ListItemButton>
        <ListItemButton onClick={handleLogout} sx={{ px: 2.5, py: 1, mx: 1, borderRadius: '10px', color: '#cc1b1b' }}>
          <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 14, color: '#cc1b1b' }} />
        </ListItemButton>
      </List>
      </Box>

      {/* Profile — anchored to drawer bottom with comfortable inset */}
      <Box
        sx={{
          flexShrink: 0,
          pt: 1.5,
          px: 1,
          pb: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${CWY_COLORS.border}`,
          bgcolor: isDark ? '#111' : '#fff',
        }}
      >
      <Box
        component="button"
        type="button"
        onClick={() => {
          setDrawerOpen(false);
          navigate('/dashboard');
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          px: 1.5,
          py: 1.25,
          border: 'none',
          borderRadius: '12px',
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          cursor: 'pointer',
          textAlign: 'left',
          '&:hover': {
            bgcolor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)',
          },
        }}
      >
        <MembershipAvatar
          name={client?.name}
          src={client?.profile_picture}
          tier={membership.tier}
          showRing={isLive}
          size="md"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: CWY_COLORS.textPrimary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
            }}
          >
            {client?.name || 'My Account'}
          </Typography>
          {client?.email ? (
            <Typography
              sx={{
                fontSize: 12,
                color: CWY_COLORS.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                mt: 0.25,
              }}
            >
              {client.email}
            </Typography>
          ) : null}
          {isLive && membership.tier ? (
            <Box sx={{ mt: 0.5, display: 'inline-flex' }}>
              <MembershipTierBadge tier={membership.tier} />
            </Box>
          ) : null}
        </Box>
        <ChevronRightIcon sx={{ color: CWY_COLORS.textSecondary, fontSize: 20, flexShrink: 0 }} />
      </Box>
      </Box>
    </Drawer>

    <Box
      component="header"
      sx={{
        position: { xs: 'fixed', sm: 'sticky' },
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        width: '100%',
        bgcolor: { xs: 'transparent', sm: CWY_COLORS.pageBg },
        borderBottom: { xs: 'none', sm: `1px solid ${CWY_COLORS.border}` },
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: LAYOUT_MAX_WIDTH,
          mx: 'auto',
          px: { xs: 1.5, sm: 2, md: 3 },
          height: { xs: 'auto', sm: TOPBAR_HEIGHT },
          minHeight: { xs: TOPBAR_HEIGHT, sm: TOPBAR_HEIGHT },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, md: 1.5 },
        }}
      >
        {/* Hamburger — mobile only, LEFT side */}
        <IconButton
          size="small"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          sx={{
            display: { xs: 'flex', sm: 'none' },
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            width: 34,
            height: 34,
            borderRadius: '8px',
            flexShrink: 0,
            mt: { xs: 'calc(18px + env(safe-area-inset-top, 0px))', sm: 0 },
            mb: { xs: '12px', sm: 0 },
            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        {/* Logo — hidden on mobile, visible on sm+ */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 0 }}>
          <Box
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
          >
            <Box
              component="img"
              src={cwFavicon}
              alt="CWY"
              sx={{ width: 34, height: 34, display: { xs: 'block', sm: 'none' } }}
            />
            <Box
              component="img"
              src={cwLogo}
              alt="Canadawale Yar"
              sx={{ height: 32, display: { xs: 'none', sm: 'block' } }}
            />
          </Box>
        </Box>

        {/* Primary nav — hidden on mobile */}
        <Box
          component="nav"
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 auto',
            minWidth: 0,
            ml: { md: 1 },
            overflow: 'visible',
            gap: { xs: 0, sm: 0.25 },
          }}
        >
          {navItems.map((item) => {
            const active = item.match(location.pathname);
            const badgeCount = item.badgeCount ?? 0;

            let iconNode = item.icon;
            if (item.label === 'Notifications') {
              iconNode = active ? navIcon(NotificationsIcon) : navIcon(NotificationsNoneOutlinedIcon);
            }

            const icon = withNavBadge(iconNode, badgeCount);
            const ariaLabel =
              badgeCount > 0 ? `${item.label}, ${badgeCount} unread` : item.label;

            return (
              <Tooltip key={item.label} title={item.label} disableHoverListener={false}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    verticalAlign: 'middle',
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => handleNav(item)}
                    aria-label={ariaLabel}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      ...navButtonSx(active),
                      overflow: 'visible',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 24, lg: 20 },
                        height: { xs: 24, lg: 20 },
                        lineHeight: 0,
                      }}
                    >
                      {icon}
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                        lineHeight: 1.2,
                        mt: 0.25,
                        fontWeight: active ? 600 : 400,
                        display: 'block',
                        width: '100%',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* Right actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexShrink: 0,
            ml: 'auto',
            pl: 0.5,
          }}
        >
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <CountrySelector variant="light" />
          </Box>

          {/* Theme toggle — hidden on mobile (lives in drawer) */}
          <IconButton
            size="small"
            onClick={() => setMode(isDark ? 'light' : 'dark')}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              color: CWY_COLORS.textPrimary,
              bgcolor: 'transparent',
              width: 30,
              height: 30,
              borderRadius: '50%',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                transform: 'scale(1.1)',
              }
            }}
            title={isDark ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
          >
            {isDark ? (
              <Box component="span" sx={{ display: 'inline-block', transform: 'scaleX(-1)' }}>
                🌙
              </Box>
            ) : (
              '☀️'
            )}
          </IconButton>

          <IconButton
            size="small"
            onClick={(e) => setAccountAnchor(e.currentTarget)}
            aria-label="Account menu"
            sx={{
              p: 0.25,
              ml: 0.5,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            <MembershipAvatar
              name={client?.name}
              src={(client as { profile_picture?: string })?.profile_picture}
              tier={membership.tier}
              showRing={isLive}
              size="sm"
            />
          </IconButton>

          {isLive && membership.tier && (
            <Box sx={{ ml: 0.75, display: 'inline-flex' }}>
              <MembershipTierBadge tier={membership.tier} />
            </Box>
          )}

          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 200, mt: 0.5 } } }}
          >
            <MenuItem onClick={() => { setAccountAnchor(null); navigate('/dashboard'); }}>
              <PersonOutlineIcon fontSize="small" sx={{ mr: 1.5 }} />
              My Account
            </MenuItem>
            <MenuItem onClick={() => { setAccountAnchor(null); navigate('/membership'); }}>
              <CardMembershipOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
              {isLive && membership.tier
                ? `Membership · ${membershipTierLabel}`
                : 'Membership'}
            </MenuItem>
            <MenuItem onClick={() => { setAccountAnchor(null); navigate('/profile/wallet'); }}>
              <AccountBalanceWalletOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
              My Crypto Wallet
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
              Sign out
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
    </>
  );
};

export { LAYOUT_MAX_WIDTH, TOPBAR_HEIGHT };
